import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Play, Quote, Mail, MapPin, Send, Star, Lock, Heart, Sparkles, ChevronLeft, ChevronRight, ShoppingBag, Tag, Plus } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { mediaUrl, embedSourceUrl } from "@/lib/api";
import { MaskedLines, LetterReveal, Reveal, StaggerGroup, StaggerItem } from "@/components/site/Reveal";
import MediaLightbox from "@/components/site/MediaLightbox";
import ShareQR from "@/components/site/ShareQR";
import StillInside from "@/components/site/StillInside";

/* Render text with *emphasis* wrapped in gold italic */
const renderEmphasis = (text) => {
  if (!text) return null;
  return String(text).split(/\*(.+?)\*/g).map((p, i) =>
    i % 2 === 1
      ? <span key={i} className="italic gold-gradient-text">{p}</span>
      : <span key={i}>{p}</span>
  );
};

/* ---------------- Hero ---------------- */
function Hero({ settings }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = settings?.hero_lines || ["Inside of", "Mental", "Injustice"];

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        {settings?.hero_video_url ? (
          <>
            <HeroVideo url={settings.hero_video_url} />
            <div className="absolute inset-0 bg-imi-bg/70" />
            <div className="absolute inset-0 bg-gradient-to-t from-imi-bg via-imi-bg/40 to-imi-bg/70" />
          </>
        ) : settings?.hero_image && (
          <>
            <img
              src={mediaUrl({ media_id: settings.hero_image })}
              alt="" className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-imi-bg/85" />
            <div className="absolute inset-0 bg-gradient-to-t from-imi-bg via-imi-bg/40 to-imi-bg/70" />
          </>
        )}
        {/* Cinematic drifting bokeh / smoke — always visible; primary visual when no hero image */}
        <div className="smoke-layer" data-testid="hero-smoke" aria-hidden="true">
          <div className="smoke-blob a" />
          <div className="smoke-blob b" />
          <div className="smoke-blob c" />
          <div className="smoke-blob d" />
        </div>
        <div className="absolute inset-0 vignette" />
      </motion.div>

      {/* floating dust */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        {[...Array(14)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-imi-gold/40"
            style={{ width: 2 + (i % 3), height: 2 + (i % 3), left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%` }}
            animate={{ y: [0, -30, 0], opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 6 + (i % 5), repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
          />
        ))}
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:pr-[420px] pt-28 pb-20 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 1 }}
          className="text-[11px] md:text-sm uppercase tracking-[0.35em] text-imi-gold mb-6"
        >
          {settings?.hero_kicker || "Survivor-led Media • Production • Advocacy"}
        </motion.p>
        <h1 className="font-serif text-imi-text text-[15vw] sm:text-[11vw] md:text-[8rem] lg:text-[9.5rem] leading-[0.9] tracking-tighter">
          {lines.map((l, i) => (
            <span key={i} className="block">
              <LetterReveal
                text={l}
                className={i === lines.length - 1 ? "gold-gradient-text italic" : ""}
                stagger={0.05}
                delay={0.3 + i * 0.35}
              />
            </span>
          ))}
        </h1>
        <div className="mt-8 flex flex-col md:flex-row md:items-end gap-8 md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
            className="text-imi-text/70 text-base md:text-lg max-w-md leading-relaxed"
          >
            {settings?.hero_sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
            className="flex gap-4"
          >
            <a href="#films" data-testid="hero-watch-btn" className="group inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-7 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors">
              <Play size={16} /> Watch
            </a>
            <a href="#productions" data-testid="hero-explore-btn" className="group inline-flex items-center gap-3 border border-imi-border text-imi-text px-7 py-4 text-sm uppercase tracking-[0.15em] hover:border-imi-gold hover:text-imi-gold transition-colors">
              Explore <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Founder card — right side, pushed up near the nav */}
      <motion.div
        initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:block absolute right-10 top-24 z-10 w-[350px] xl:w-[400px]"
        data-testid="hero-founder-card"
      >
        <div className="relative border border-imi-gold/30 bg-imi-surface/75 backdrop-blur-md">
          <div className="overflow-hidden aspect-[5/4]">
            <img
              src={settings?.founder_image ? mediaUrl({ media_id: settings.founder_image }) : "/founder-harold.png"} alt="Harold Cunningham Jr., Founder & CEO"
              className="w-full h-full object-cover" style={{ objectPosition: "center 25%" }}
            />
          </div>
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-imi-gold mb-1">Founder &amp; CEO</div>
            <div className="font-serif text-xl text-imi-text leading-tight mb-3">Harold Cunningham Jr.</div>
            <p className="text-imi-text/60 text-[12px] leading-relaxed">{settings?.founder_bio}</p>
          </div>
          <span className="absolute -top-px -left-px w-6 h-6 border-t-2 border-l-2 border-imi-gold" />
          <span className="absolute -bottom-px -right-px w-6 h-6 border-b-2 border-r-2 border-imi-gold" />
        </div>
      </motion.div>


      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 hidden md:flex items-center gap-3 text-imi-text/40 text-[11px] uppercase tracking-[0.25em]"
      >
        <span>Scroll</span>
        <div className="w-16 h-px bg-imi-gold/50 overflow-hidden">
          <motion.div className="h-full w-1/2 bg-imi-gold" animate={{ x: [-32, 64] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
        </div>
      </motion.div>
    </section>
  );
}

/* ---------------- Marquee band ---------------- */
function Band({ text }) {
  const items = (text || "").split("•").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="border-y border-imi-border bg-imi-surface py-5 overflow-hidden">
      <Marquee speed={30} gradient={false} autoFill>
        {items.map((t, i) => (
          <span key={i} className="mx-8 text-2xl md:text-4xl font-serif italic text-imi-text/80 flex items-center gap-8">
            {t} <span className="text-imi-gold not-italic">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------------- Stats ---------------- */
function Stats({ stats = [] }) {
  const startYear = new Date().getFullYear();
  const endYear = startYear + 3;
  return (
    <section id="goals" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
      <Reveal className="mb-12 md:mb-16 max-w-2xl">
        <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">The Mission Ahead · {startYear}–{endYear}</p>
        <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
          Our goals for the <span className="italic gold-gradient-text">next three years</span>.
        </h2>
        <p className="mt-6 text-imi-text/60 text-lg leading-relaxed">
          Bold, measurable targets we&rsquo;re building toward — every film, investigation and policy win moves us closer.
        </p>
      </Reveal>
      <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 gap-px bg-imi-border border border-imi-border">
        {stats.map((s, i) => (
          <StaggerItem key={i} className="bg-imi-bg p-8 md:p-12 relative">
            <span className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.25em] text-imi-gold/70 border border-imi-gold/30 px-2 py-1">Goal</span>
            <div className="font-serif text-imi-goldlight text-5xl md:text-7xl leading-none">{s.value}</div>
            <div className="mt-4 text-[11px] md:text-xs uppercase tracking-[0.25em] text-imi-text/50">{s.label}</div>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </section>
  );
}

/* ---------------- Mission Band — prominent single-line manifesto near the top ---------------- */
function MissionBand({ settings }) {
  const mission = settings?.mission;
  if (!mission) return null;
  const campaignOn = settings?.campaign_enabled !== false;
  const campaignImg = settings?.campaign_image ? mediaUrl({ media_id: settings.campaign_image }) : "/reelect-harold.jpg";
  return (
    <section
      id="mission-band"
      data-testid="mission-band"
      className="relative border-b border-imi-border bg-gradient-to-b from-imi-bg via-imi-surface/40 to-imi-bg py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #d4af5a 0%, transparent 40%), radial-gradient(circle at 80% 70%, #d4af5a 0%, transparent 40%)" }} />
      <div className={`${campaignOn ? "max-w-[1400px]" : "max-w-[1100px]"} mx-auto px-6 md:px-10 relative z-10`}>
        <div className={campaignOn ? "grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" : ""}>
        <Reveal className={campaignOn ? "lg:col-span-7" : ""}>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-10 bg-imi-gold" />
            <p className="text-[11px] uppercase tracking-[0.4em] text-imi-gold">{settings?.mission_band_kicker || "Our Mission"}</p>
            <div className="h-px flex-1 bg-imi-border" />
          </div>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-imi-goldlight">
            {renderEmphasis(settings?.mission_band_title || "We refuse to let *the caged voices* stay unheard.")}
          </h2>
          <p className="mt-8 md:mt-10 text-imi-text/70 text-lg md:text-xl leading-relaxed max-w-3xl">
            {mission}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#support" data-testid="mission-band-support" className="inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors">
              Support the mission <Heart size={14} />
            </a>
            <a href="#manifesto-wall" data-testid="mission-band-sign" className="inline-flex items-center gap-2 border border-imi-gold/40 text-imi-gold px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-imi-gold/10 transition-colors">
              Sign the manifesto <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
        {campaignOn && <CampaignCard settings={settings} img={campaignImg} />}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Campaign pamphlet — re-election card with slogan ribbon ---------------- */
function CampaignCard({ settings, img }) {
  const Wrap = settings?.campaign_link ? "a" : "div";
  const wrapProps = settings?.campaign_link ? { href: settings.campaign_link, target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Reveal delay={0.15} className="lg:col-span-5">
      <div className="relative max-w-[440px] mx-auto lg:ml-auto lg:mr-0" data-testid="campaign-card">
        <div className="mb-4 flex items-center gap-3">
          <Star size={12} className="text-imi-gold" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-imi-gold" data-testid="campaign-kicker">{settings?.campaign_kicker || "Re-elect · ANC Commissioner 7F08"}</p>
        </div>
        <h3 className="font-serif text-2xl md:text-3xl leading-[1.15] tracking-tight text-imi-text mb-5" data-testid="campaign-slogan">
          {renderEmphasis(settings?.campaign_slogan || "From *Solitary Confinement* to ANC Commissioner.")}
        </h3>
        <Wrap {...wrapProps} className="group relative block border border-imi-gold/40 bg-imi-surface/60 backdrop-blur-sm p-2 shadow-[0_30px_80px_-30px_rgba(212,175,90,0.35)]">
          <div className="relative overflow-hidden">
            <img src={img} alt="Re-elect Harold Cunningham for ANC Commissioner 7F08" data-testid="campaign-image" className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]" />
          </div>
          <div className="px-4 py-3 flex items-center justify-between gap-3 border-t border-imi-gold/20">
            <div className="font-serif text-imi-goldlight text-base leading-tight" data-testid="campaign-caption">{settings?.campaign_caption || "Vote Harold Cunningham for ANC Commissioner 7F08"}</div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-imi-gold/70 whitespace-nowrap">Campaign pamphlet</div>
          </div>
          <span className="absolute -top-px -left-px w-6 h-6 border-t-2 border-l-2 border-imi-gold" />
          <span className="absolute -bottom-px -right-px w-6 h-6 border-b-2 border-r-2 border-imi-gold" />
        </Wrap>
      </div>
    </Reveal>
  );
}

/* ---------------- Founder's Note — compact intro to Harold Cunningham Jr. ---------------- */
function FoundersNote({ settings }) {
  const bio = settings?.founder_bio;
  if (!bio) return null;
  const name = settings?.founder_name || "Harold Cunningham Jr.";
  const role = settings?.founder_role || "Founder & CEO";
  const image = settings?.founder_image ? mediaUrl({ media_id: settings.founder_image }) : null;
  return (
    <section
      id="founders-note"
      data-testid="founders-note"
      className="relative border-b border-imi-border bg-imi-surface/40 py-20 md:py-24"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <Reveal className="md:col-span-4">
            <div className="relative">
              {image ? (
                <img src={image} alt={name} className="w-full aspect-[4/5] object-cover border border-imi-gold/30" data-testid="founders-note-image" />
              ) : (
                <div className="w-full aspect-[4/5] bg-imi-elevated border border-imi-gold/30 flex items-center justify-center">
                  <Quote size={64} className="text-imi-gold/30" />
                </div>
              )}
              <div className="absolute -bottom-4 -right-4 hidden md:flex w-24 h-24 items-center justify-center bg-imi-gold text-imi-bg">
                <Quote size={40} />
              </div>
            </div>
          </Reveal>

          <Reveal className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-imi-gold" />
              <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold">A word from the founder</p>
            </div>
            <p className="font-serif text-imi-text/85 text-xl md:text-2xl leading-[1.55] tracking-tight" data-testid="founders-note-bio">
              {bio.length > 520 ? `${bio.slice(0, 520).trim()}…` : bio}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-px w-12 bg-imi-gold" />
              <div>
                <div className="font-serif text-imi-goldlight text-xl" data-testid="founders-note-name">{name}</div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-imi-text/50 mt-1">{role}</div>
              </div>
            </div>
            <a href="#story" data-testid="founders-note-cta" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-imi-gold hover:text-imi-goldlight transition-colors">
              Read the full story <ArrowRight size={14} />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Hero background video — muted autoplaying YouTube/Vimeo ---------------- */
function HeroVideo({ url }) {
  const src = (() => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      if (host === "youtube.com" || host === "m.youtube.com") {
        const id = u.searchParams.get("v");
        if (!id) return null;
        return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&modestbranding=1&playsinline=1&rel=0`;
      }
      if (host === "youtu.be") {
        const id = u.pathname.slice(1);
        return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&modestbranding=1&playsinline=1&rel=0`;
      }
      if (host.includes("vimeo.com")) {
        const id = u.pathname.split("/").filter(Boolean).pop();
        return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=1&controls=0`;
      }
      return url;
    } catch { return null; }
  })();
  if (!src) return null;
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="hero-video-bg">
      <iframe
        src={src}
        title="Hero background video"
        allow="autoplay; encrypted-media; picture-in-picture"
        className="absolute top-1/2 left-1/2 w-[177.77vh] h-[100vh] min-w-full min-h-full -translate-x-1/2 -translate-y-1/2"
        frameBorder="0"
      />
    </div>
  );
}

/* ---------------- Ambassadors — voices of the movement ---------------- */
function Ambassadors({ settings }) {
  const list = (settings?.ambassadors || []).filter((a) => a && (a.name || "").trim());
  const [expanded, setExpanded] = useState({}); // { [idx]: bool }
  if (list.length === 0) return null;
  return (
    <section id="ambassadors" className="relative border-b border-imi-border py-24 md:py-32" data-testid="ambassadors-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="max-w-3xl mb-14 md:mb-20">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">{settings?.ambassadors_kicker || "Voices of the Movement"}</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            {renderEmphasis(settings?.ambassadors_title || "The people *fueling the fight*.")}
          </h2>
          {settings?.ambassadors_subtitle && (
            <p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed">{settings.ambassadors_subtitle}</p>
          )}
        </Reveal>
        <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 items-start">
          {list.map((a, i) => {
            const img = a.photo ? mediaUrl({ media_id: a.photo }) : null;
            const isOpen = !!expanded[i];
            const shortBio = a.bio && a.bio.length > 220 ? `${a.bio.slice(0, 220).trim()}…` : a.bio;
            return (
              <StaggerItem key={i}>
                <article className="group border border-imi-border bg-imi-surface/40 overflow-hidden hover:border-imi-gold/40 transition-colors" data-testid={`ambassador-${i}`}>
                  <div className={`${img ? "aspect-[4/3]" : "aspect-[16/9]"} bg-imi-elevated relative overflow-hidden`}>
                    {img ? (
                      <img src={img} alt={a.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Quote size={56} className="text-imi-gold/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-imi-bg/80 via-transparent to-transparent" />
                    {a.city && (
                      <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] text-imi-gold bg-imi-bg/70 backdrop-blur px-2 py-1 border border-imi-gold/30">{a.city}</div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="font-serif text-imi-text text-xl md:text-2xl leading-tight">{a.name}</div>
                    {a.role && <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-imi-gold">{a.role}</div>}
                    {a.bio && (
                      <>
                        <p className="mt-4 text-imi-text/70 text-[14px] md:text-[15px] leading-relaxed whitespace-pre-line" data-testid={`ambassador-bio-${i}`}>
                          {isOpen ? a.bio : shortBio}
                        </p>
                        {a.bio.length > 220 && (
                          <button
                            type="button"
                            onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                            data-testid={`ambassador-toggle-${i}`}
                            className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-imi-gold hover:text-imi-goldlight transition-colors"
                          >
                            {isOpen ? "Read less" : "Read more"} <ArrowRight size={12} className={isOpen ? "rotate-90 transition-transform" : "transition-transform"} />
                          </button>
                        )}
                      </>
                    )}
                    {a.link && (
                      <a href={a.link} target="_blank" rel="noopener noreferrer" className="mt-3 ml-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-imi-gold/70 hover:text-imi-goldlight" data-testid={`ambassador-link-${i}`}>
                        Website / Social ↗
                      </a>
                    )}
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* ---------------- Biography — compact "Featured story" band linking to /harold ---------------- */
function Biography({ settings }) {
  const bio = settings?.founder_biography_full;
  if (!bio) return null;
  const imgId = settings?.biography_image;
  const img = imgId ? mediaUrl({ media_id: imgId }) : "/harold-podium.png";
  return (
    <section id="biography-cta" className="relative border-b border-imi-border py-20 md:py-24 bg-imi-elevated" data-testid="biography-cta">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden border border-imi-gold/30 bg-imi-surface/40">
              <img src={img} alt="Harold Cunningham Jr." data-testid="biography-cta-image" className="w-full h-auto block" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-4" data-testid="biography-cta-kicker">{settings?.biography_kicker || "The Full Story"}</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight" data-testid="biography-cta-title">
              {renderEmphasis(settings?.biography_title || "From *ADX solitary* to elected office.")}
            </h2>
            <p className="mt-6 text-imi-text/70 text-base md:text-lg leading-relaxed max-w-xl whitespace-pre-line" data-testid="biography-cta-text">
              {settings?.biography_text || "Thirty years incarcerated. Twenty in solitary. A landmark 2016 civil rights settlement. Election in 2025 as the first-ever incarcerated Vice Chair of a D.C. Advisory Neighborhood Commission. Read the full biography of IMI's founder."}
            </p>
            <Link to="/harold" data-testid="biography-cta-btn" className="mt-8 inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-6 py-4 text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors">
              {settings?.biography_button || "Read Harold's biography"} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Tell Their Story CTA — homepage entry point to /tell-their-story ---------------- */
function TellTheirStoryCTA() {
  return (
    <section id="tell-their-story-cta" className="relative border-b border-imi-border py-20 md:py-28 bg-gradient-to-b from-imi-bg via-imi-surface/30 to-imi-bg" data-testid="tell-their-story-cta">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 items-center border border-imi-gold/25 bg-imi-elevated p-8 md:p-12">
            <div className="md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-4">Someone you love is still inside?</p>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">
                Tell us <span className="italic gold-gradient-text">their story</span>.
              </h2>
              <p className="mt-5 text-imi-text/70 text-base md:text-lg leading-relaxed max-w-xl">
                Share what you know — a name, a facility, a story — and help us bring their voice out of isolation.
                Anonymous submissions welcome. Nothing is published without your explicit approval.
              </p>
              <div className="mt-4 flex items-center gap-2 text-imi-gold/80 text-xs">
                <Lock size={14} /> <span>Confidential · Journalist-source protected</span>
              </div>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/tell-their-story"
                data-testid="tell-their-story-cta-btn"
                className="inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-6 py-4 text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors"
              >
                Share their story <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Screening Request Form — supporters host local IMI screenings ---------------- */
function ScreeningRequestForm() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", city: "", audience_size: "", film: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setLoading(true);
    try {
      await api.post("/screening-request", form);
      setDone(true);
      toast.success("Request received — we'll follow up shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <section id="host-screening" className="border-t border-imi-border bg-imi-surface py-24 md:py-32" data-testid="screening-request-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Host a Screening</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Bring an IMI film to <span className="italic gold-gradient-text">your community</span>.
            </h2>
            <p className="mt-6 text-imi-text/70 text-lg leading-relaxed max-w-md">
              Host a public or invitational screening at your college, church, community centre, or event.
              We'll help with everything — film selection, promotional assets, and a live Q&amp;A when possible.
            </p>
            <ul className="mt-6 space-y-2 text-imi-text/60 text-sm max-w-md">
              <li className="flex items-start gap-3"><Star size={12} className="text-imi-gold mt-1 shrink-0" /> Free for nonprofits, schools, and community orgs</li>
              <li className="flex items-start gap-3"><Star size={12} className="text-imi-gold mt-1 shrink-0" /> Discussion guides and press kit included</li>
              <li className="flex items-start gap-3"><Star size={12} className="text-imi-gold mt-1 shrink-0" /> Optional Q&amp;A with the founder or featured storyteller</li>
            </ul>
          </Reveal>

          <Reveal>
            {done ? (
              <div className="border border-imi-gold/40 bg-imi-bg/70 p-10 text-center" data-testid="screening-thanks">
                <Sparkles size={40} className="text-imi-gold mx-auto mb-4" />
                <div className="font-serif text-2xl text-imi-goldlight mb-3">Request received</div>
                <p className="text-imi-text/60 text-sm max-w-sm mx-auto">Thank you for choosing to bring IMI to your community. We'll be in touch within a few business days with next steps.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-imi-border bg-imi-bg/60 p-6 md:p-8 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.name} onChange={set("name")} data-testid="screening-name" placeholder="Your name" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="screening-email" placeholder="Email" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input value={form.organization} onChange={set("organization")} data-testid="screening-org" placeholder="Organization (optional)" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
                  <input value={form.city} onChange={set("city")} data-testid="screening-city" placeholder="City, State" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <select value={form.audience_size} onChange={set("audience_size")} data-testid="screening-audience" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text w-full transition-colors">
                    <option value="" className="bg-imi-bg">Expected audience size</option>
                    <option value="under-25" className="bg-imi-bg">Under 25</option>
                    <option value="25-100" className="bg-imi-bg">25 – 100</option>
                    <option value="100-500" className="bg-imi-bg">100 – 500</option>
                    <option value="500+" className="bg-imi-bg">500+</option>
                  </select>
                  <input value={form.film} onChange={set("film")} data-testid="screening-film" placeholder="Which film (or 'any')" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
                </div>
                <textarea rows={3} maxLength={2000} value={form.notes} onChange={set("notes")} data-testid="screening-notes" placeholder="Anything else? Preferred date, venue, purpose…" className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors resize-none" />
                <button type="submit" disabled={loading} data-testid="screening-submit" className="inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors disabled:opacity-60">
                  {loading ? "Sending…" : "Request a screening"} <Send size={14} />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Press Strip — "As seen in" trust band ---------------- */
function PressStrip({ settings }) {
  const items = (settings?.press_mentions || []).filter((p) => p && (p.name || "").trim());
  if (items.length === 0) return null;
  const kicker = settings?.press_kicker || "As seen in";
  return (
    <section id="press-strip" data-testid="press-strip" className="border-y border-imi-border bg-imi-bg/60">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
        <div className="flex items-center gap-3 shrink-0">
          <div className="h-px w-8 bg-imi-gold/60" />
          <span className="text-[10px] uppercase tracking-[0.35em] text-imi-gold whitespace-nowrap">{kicker}</span>
        </div>
        <div className="flex-1 flex flex-wrap items-center justify-center md:justify-start gap-x-8 md:gap-x-12 gap-y-4">
          {items.map((p, i) => {
            const logoSrc = p.logo ? mediaUrl({ media_id: p.logo }) : null;
            const inner = logoSrc ? (
              <img
                src={logoSrc}
                alt={p.name || "Press outlet"}
                className="h-8 md:h-10 w-auto max-w-[180px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                style={{ filter: "brightness(0) invert(1) opacity(0.7)" }}
                onError={(e) => { e.currentTarget.style.filter = "none"; }}
              />
            ) : (
              <span className="font-serif text-imi-text/70 text-lg md:text-xl tracking-tight hover:text-imi-goldlight transition-colors">
                {p.name}
              </span>
            );
            return p.url ? (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`press-outlet-${i}`}
                className="inline-flex items-center gap-2 group"
              >
                {inner}
                {!logoSrc && <span className="text-imi-gold/40 group-hover:text-imi-gold text-xs transition-colors">↗</span>}
              </a>
            ) : (
              <span key={i} data-testid={`press-outlet-${i}`} className="inline-flex items-center">{inner}</span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Manifesto ---------------- */
const CHAPTERS = [
  { n: "01", title: "Lived experience is evidence.", body: "We center those who survived solitary confinement and the justice system — because the truth of a system is written on the people it holds." },
  { n: "02", title: "Stories move policy.", body: "Every film, podcast and investigation is engineered to reach hearts and halls of power alike, turning testimony into reform." },
  { n: "03", title: "We do not look away.", body: "Uncompromising, evidence-driven journalism that documents institutional harm — and refuses to let it stay invisible." },
];

function Manifesto({ settings }) {
  const chapters = (settings?.manifesto && settings.manifesto.length > 0) ? settings.manifesto : CHAPTERS;
  return (
    <section id="manifesto" className="relative border-y border-imi-border py-24 md:py-36" data-testid="manifesto-scroll">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="mb-14 md:mb-20 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">{settings?.manifesto_kicker || "The Manifesto"}</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">
            {renderEmphasis(settings?.manifesto_title || "Three convictions that *drive everything* we make.")}
          </h2>
        </Reveal>

        <StaggerGroup className="grid md:grid-cols-3 gap-6 md:gap-8">
          {chapters.map((c, idx) => (
            <StaggerItem key={`${c.n}-${idx}`}>
              <article
                data-testid={`manifesto-chapter-${idx}`}
                className="relative h-full border border-imi-border bg-imi-surface/40 p-8 md:p-10 flex flex-col overflow-hidden group hover:border-imi-gold/40 transition-colors"
              >
                <span className="pointer-events-none select-none font-serif text-imi-golddark/10 text-[10rem] md:text-[12rem] leading-none absolute -top-6 -right-4">
                  {c.n}
                </span>
                <div className="relative z-10 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="font-serif text-imi-gold text-lg tracking-wider">{c.n}</span>
                    <div className="h-px flex-1 bg-imi-gold/30 group-hover:bg-imi-gold/60 transition-colors" />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight text-imi-text mb-5">{c.title}</h3>
                  <p className="text-imi-text/60 text-base md:text-[17px] leading-relaxed">{c.body}</p>
                </div>
              </article>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <div className="mt-10 md:mt-14 flex items-center gap-3 text-imi-text/40">
          <div className="h-px flex-1 bg-imi-border" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-imi-gold" data-testid="manifesto-progress">{chapters.length} convictions</span>
          <div className="h-px flex-1 bg-imi-border" />
        </div>
      </div>
    </section>
  );
}

/* ---------------- 6x9 Cell — Harold's twenty years in solitary ---------------- */
const CELL_LINES = [
  { t: "Six feet by nine feet.", cls: "font-serif text-4xl md:text-6xl text-imi-text" },
  { t: "Twenty years.",           cls: "font-serif italic text-4xl md:text-6xl gold-gradient-text" },
  { t: "No windows.",             cls: "font-serif text-2xl md:text-4xl text-imi-text/80" },
  { t: "No voices.",              cls: "font-serif text-2xl md:text-4xl text-imi-text/80" },
  { t: "No time.",                cls: "font-serif text-2xl md:text-4xl text-imi-text/80" },
  { t: "I lived it.",             cls: "mt-8 font-serif text-3xl md:text-5xl text-imi-text" },
  { t: "Now I tell it — so no one else has to.", cls: "font-serif italic text-2xl md:text-3xl gold-gradient-text" },
];

function SixByNineCell({ settings }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start 90%", "end 10%"] });
  // Golden scan line pulled downward as user scrolls through the section
  const scanY = useTransform(scrollYProgress, [0, 1], ["-5%", "105%"]);
  const lines = settings?.cell_lines?.length ? settings.cell_lines.map((t) => ({ t, cls: "font-serif text-2xl md:text-4xl text-imi-text/80" })) : CELL_LINES;
  return (
    <section
      id="cell"
      ref={sectionRef}
      className="relative border-t border-imi-border bg-imi-bg py-24 md:py-32 overflow-hidden"
      data-testid="six-by-nine-cell"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="mb-14 md:mb-20 max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.cell_kicker || "Six by Nine"}</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            {renderEmphasis(settings?.cell_title || "The room that *shaped a mission*.")}
          </h2>
          <p className="mt-6 text-imi-text/60 text-base md:text-lg leading-relaxed">
            {settings?.cell_intro || "This is the actual footprint of a solitary confinement cell. Every line inside was earned — not written."}
          </p>
        </Reveal>

        {/* The cell footprint. 6:9 aspect (portrait) — outlined in gold, with a scanning light */}
        <div className="mx-auto max-w-[520px] md:max-w-[600px]">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[6/9] border border-imi-gold/60 bg-imi-surface/40 backdrop-blur-sm overflow-hidden"
            data-testid="cell-frame"
          >
            {/* Optional cell image — ghosted background so the stanza stays legible on top */}
            {settings?.cell_image && (
              <>
                <img
                  src={mediaUrl({ media_id: settings.cell_image })}
                  alt=""
                  data-testid="cell-image"
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-imi-bg/60" />
                <div className="absolute inset-0 bg-gradient-to-b from-imi-bg/70 via-imi-bg/30 to-imi-bg/80" />
              </>
            )}

            {/* Corner brackets */}
            <span className="absolute -top-px -left-px w-8 h-8 border-t-2 border-l-2 border-imi-gold" />
            <span className="absolute -top-px -right-px w-8 h-8 border-t-2 border-r-2 border-imi-gold" />
            <span className="absolute -bottom-px -left-px w-8 h-8 border-b-2 border-l-2 border-imi-gold" />
            <span className="absolute -bottom-px -right-px w-8 h-8 border-b-2 border-r-2 border-imi-gold" />

            {/* Dimensions labels */}
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.35em] text-imi-gold whitespace-nowrap">6 ft</span>
            <span className="absolute top-1/2 -left-10 -translate-y-1/2 -rotate-90 text-[10px] uppercase tracking-[0.35em] text-imi-gold whitespace-nowrap origin-center">9 ft</span>

            {/* Sweeping gold scan line (scroll-linked) */}
            <motion.div
              style={{ y: scanY }}
              aria-hidden="true"
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-imi-gold to-transparent shadow-[0_0_18px_rgba(212,175,90,0.6)]"
            />

            {/* Stanza inside the cell — each line staggers in as user scrolls */}
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center gap-1.5 md:gap-2">
              {lines.map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.28, ease: [0.22, 1, 0.36, 1] }}
                  className={line.cls || "font-serif text-2xl md:text-4xl text-imi-text/80"}
                  data-testid={`cell-line-${i}`}
                >
                  {line.t}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Attribution beneath the cell */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, delay: 0.6 }}
            className="mt-8 text-center text-imi-text/50 text-xs md:text-sm uppercase tracking-[0.3em]"
          >
            — Harold Cunningham Jr., Founder
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Manifesto Wall — "Sign the Manifesto" + slow marquee of signatures ---------------- */
function ManifestoWall() {
  const [form, setForm] = useState({ name: "", location: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [total, setTotal] = useState(0);

  const load = () => api.get("/manifesto/signatures?limit=500")
    .then((r) => { setSignatures(r.data.items || []); setTotal(r.data.total || 0); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please add your name."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/manifesto/sign", form);
      toast.success(data.message || "Signed — thank you.");
      setForm({ name: "", location: "", message: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save signature.");
    } finally { setLoading(false); }
  };

  // Split into two rows for a richer scrolling wall (odd/even)
  const rowA = signatures.filter((_, i) => i % 2 === 0);
  const rowB = signatures.filter((_, i) => i % 2 === 1);

  const Chip = ({ s }) => (
    <span data-testid={`sig-chip-${s.id}`} className="mx-2 md:mx-3 inline-flex items-center gap-2 border border-imi-gold/30 bg-imi-bg/70 backdrop-blur px-4 py-2 text-sm">
      <Star size={12} className="text-imi-gold" />
      <span className="font-serif text-imi-text whitespace-nowrap">{s.name}</span>
      {s.location && <span className="text-imi-text/40 text-xs whitespace-nowrap">· {s.location}</span>}
    </span>
  );

  const AddYourNameChip = ({ k }) => (
    <button
      type="button"
      key={k}
      data-testid="sig-chip-add-yours"
      onClick={() => document.querySelector('input[data-testid="signature-name"]')?.focus()}
      className="mx-2 md:mx-3 inline-flex items-center gap-2 border border-dashed border-imi-gold/40 bg-transparent px-4 py-2 text-sm text-imi-gold hover:bg-imi-gold/10 hover:border-imi-gold transition-colors whitespace-nowrap"
    >
      <Plus size={12} />
      <span className="font-serif tracking-wide">Add your name</span>
    </button>
  );

  // Pad each row to a minimum length so the marquee has enough variety
  // (mixing real signatures with "add your name" call-to-actions instead of duplicating).
  const MIN_ROW = 6;
  const pad = (row) => {
    const out = [];
    row.forEach((s) => out.push(<Chip key={`sig-${s.id}`} s={s} />));
    for (let i = out.length; i < MIN_ROW; i++) out.push(<AddYourNameChip k={`add-${i}`} />);
    return out;
  };
  const rowAContent = pad(rowA.length ? rowA : signatures);
  const rowBContent = pad(rowB);

  return (
    <section id="manifesto-wall" className="relative border-t border-imi-border bg-imi-surface py-24 md:py-32 overflow-hidden" data-testid="manifesto-wall">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Sign the Manifesto</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">Add your name to <span className="italic gold-gradient-text">the record</span>.</h2>
            <p className="mt-6 text-imi-text/60 text-base md:text-lg leading-relaxed max-w-md">
              This is a public wall of witnesses — people who refuse to look away. Your signature says
              you stand with survivors and demand humane, evidence-driven reform.
            </p>
            <div className="mt-8 inline-flex items-baseline gap-3 border border-imi-gold/30 px-5 py-3">
              <span className="font-serif text-imi-goldlight text-4xl" data-testid="signature-count">{total}</span>
              <span className="text-[11px] uppercase tracking-[0.25em] text-imi-text/60">names on the wall</span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="sign-form" className="border border-imi-border bg-imi-bg p-6 md:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  required maxLength={60}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="signature-name"
                  placeholder="Your name"
                  className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors"
                />
                <input
                  maxLength={60}
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  data-testid="sign-location"
                  placeholder="City, State (optional)"
                  className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors"
                />
              </div>
              <textarea
                rows={3} maxLength={240}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                data-testid="sign-message"
                placeholder="A note of solidarity (optional, up to 240 chars)"
                className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full resize-none transition-colors"
              />
              <button
                type="submit" disabled={loading} data-testid="sign-submit"
                className="group inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-7 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors disabled:opacity-60"
              >
                {loading ? "Signing…" : "Sign the manifesto"} <Send size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-imi-text/40 text-xs">Your name and city will appear publicly. We never share your notes.</p>
            </form>
          </Reveal>
        </div>

        {/* Wall of names — motion marquee always on; empty slots become "Add your name" call-to-actions */}
        {signatures.length > 0 && (
          <div className="mt-14 md:mt-20 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.35em] text-imi-text/40 mb-4">The Wall of Support</div>
            <Marquee speed={22} gradient gradientColor={[18, 18, 18]} gradientWidth={80} pauseOnHover>
              {rowAContent}
            </Marquee>
            <Marquee speed={18} direction="right" gradient gradientColor={[18, 18, 18]} gradientWidth={80} pauseOnHover>
              {rowBContent}
            </Marquee>
          </div>
        )}
      </div>
    </section>
  );
}



/* ---------------- Productions (bento) ---------------- */
const CATS = ["podcast", "documentary", "investigation", "all"];
function Productions({ items, settings }) {
  const [cat, setCat] = useState("podcast");
  const [openIdx, setOpenIdx] = useState(null);
  const filtered = cat === "all" ? items : items.filter((i) => (i.category || "").toLowerCase().trim() === cat);
  return (
    <section id="productions" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
        <Reveal className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.productions_kicker || "Productions"}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight">{renderEmphasis(settings?.productions_title || "The work that *bears witness*.")}</h2>
          {settings?.productions_subtitle && (
            <p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed" data-testid="productions-subtitle">{settings.productions_subtitle}</p>
          )}
        </Reveal>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c} onClick={() => setCat(c)} data-testid={`filter-${c}`}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                cat === c ? "border-imi-gold text-imi-gold bg-imi-gold/5" : "border-imi-border text-imi-text/50 hover:border-imi-text/40"
              }`}
            >{c}</button>
          ))}
        </div>
      </div>

      <StaggerGroup key={cat} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="productions-bento">
        {filtered.map((p, i) => {
          const img = mediaUrl(p) || embedThumbnail(embedSourceUrl(p));
          const playable = !!(p.video_id || embedSourceUrl(p));
          return (
            <StaggerItem key={p.id}>
              <button
                type="button"
                onClick={() => setOpenIdx(i)}
                data-testid={`production-${p.id}`}
                className="group w-full text-left border border-imi-border bg-imi-surface/40 overflow-hidden hover:border-imi-gold/50 transition-colors focus:outline-none focus:ring-2 focus:ring-imi-gold flex flex-col h-full"
              >
                <div className="aspect-video bg-imi-elevated relative overflow-hidden">
                  {img ? (
                    <img src={img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" data-testid={`production-noimg-${p.id}`}>
                      <div className="text-center">
                        <Play size={36} className="text-imi-gold/25 mx-auto mb-2" />
                        <span className="text-imi-text/30 text-[10px] uppercase tracking-[0.25em]">Preview coming soon</span>
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-imi-bg/70 via-transparent to-transparent" />
                  {p.category && (
                    <div className="absolute top-3 left-3 z-10">
                      <span className="bg-imi-bg/80 backdrop-blur text-imi-gold text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-imi-gold/30">{p.category}</span>
                    </div>
                  )}
                  {playable && (
                    <span data-testid={`production-play-${p.id}`} className="absolute top-3 right-3 z-10 h-10 w-10 rounded-full bg-imi-gold text-imi-bg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play size={14} className="ml-0.5" />
                    </span>
                  )}
                </div>
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  {p.meta && <div className="text-imi-text/50 text-[10px] uppercase tracking-[0.25em] mb-2">{p.meta}</div>}
                  <h3 className="font-serif text-imi-text text-xl md:text-[22px] leading-tight line-clamp-2">{p.title}</h3>
                  {p.excerpt && <p className="mt-3 text-imi-text/55 text-sm leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>}
                  <div className="mt-4 pt-4 border-t border-imi-border flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-imi-gold group-hover:text-imi-goldlight transition-colors">
                    <span>{playable ? "Watch now" : "View"}</span>
                    <ArrowRight size={14} />
                  </div>
                </div>
              </button>
            </StaggerItem>
          );
        })}
      </StaggerGroup>

      {filtered.length === 0 && (
        <div className="py-16 px-8 text-center border border-imi-border" data-testid="productions-empty">
          <div className="max-w-md mx-auto">
            <Play size={40} className="text-imi-gold/30 mx-auto mb-4" />
            <div className="font-serif text-imi-text text-xl mb-3">No {cat === "all" ? "productions" : cat + "s"} here yet</div>
            <p className="text-imi-text/50 text-sm leading-relaxed">
              {cat === "all"
                ? "As podcasts, documentaries, and investigations are published, they'll appear here automatically."
                : <>Add one from <span className="text-imi-gold">Admin → Content → + Add content</span>, choose <span className="text-imi-gold">Type: production</span> and <span className="text-imi-gold">Category: {cat}</span>, then paste the YouTube URL in the Embed URL field.</>}
            </p>
          </div>
        </div>
      )}

      {openIdx !== null && (
        <MediaLightbox items={filtered} startIndex={openIdx} onClose={() => setOpenIdx(null)} testIdPrefix="lightbox-production" />
      )}
    </section>
  );
}

/* ---------------- Films / Videos ---------------- */
export function Films({ items, viewAllHref, total, settings }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [cat, setCat] = useState("all");
  const cats = ["all", ...Array.from(new Set(items.map((v) => v.category).filter(Boolean)))];
  const filtered = cat === "all" ? items : items.filter((v) => v.category === cat);
  return (
    <section id="films" className="border-y border-imi-border bg-imi-surface py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.films_kicker || "Films & Videos"}</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">{renderEmphasis(settings?.films_title || "Cinema with *a conscience*.")}</h2>
            {settings?.films_subtitle && (<p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="films-subtitle">{settings.films_subtitle}</p>)}
          </Reveal>
          {viewAllHref && total > items.length && (
            <Link to={viewAllHref} data-testid="view-all-films" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-5 py-3 hover:bg-imi-gold hover:text-imi-bg transition-colors self-start">
              View all {total} films <ArrowRight size={16} />
            </Link>
          )}
        </div>
        {cats.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="films-filters">
            {cats.map((c) => (
              <button
                key={c} onClick={() => setCat(c)} data-testid={`films-filter-${c}`}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  cat === c ? "border-imi-gold text-imi-gold bg-imi-gold/5" : "border-imi-border text-imi-text/50 hover:border-imi-text/40"
                }`}
              >{c}</button>
            ))}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-5">
          {filtered.map((v, i) => {
            const uploadedId = (v.type === "video" || v.type === "inspiration") ? (v.media_id || v.video_id) : v.media_id;
            const videoSrc = uploadedId ? mediaUrl({ media_id: uploadedId }) : null;
            const vEmbed = embedSourceUrl(v);
            const hasPlayable = !!(videoSrc || vEmbed);
            const posterSrc = v.thumb_id ? mediaUrl({ thumb_id: v.thumb_id }) : (mediaUrl(v) || embedThumbnail(vEmbed) || null);
            return (
              <Reveal key={v.id} delay={i * 0.06}>
                <button
                  type="button"
                  onClick={() => hasPlayable && setOpenIdx(i)}
                  data-testid={`film-${v.id}`}
                  className="w-full text-left group/film relative overflow-hidden border border-imi-border bg-imi-bg focus:outline-none focus:ring-2 focus:ring-imi-gold"
                  disabled={!hasPlayable}
                >
                  <div className="aspect-video overflow-hidden relative bg-imi-elevated">
                    {posterSrc ? (
                      <img src={posterSrc} alt={v.title} className="w-full h-full object-cover transition-transform duration-700 group-hover/film:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-imi-elevated"><Play size={30} className="text-imi-text/20" /></div>
                    )}
                    {hasPlayable && (
                      <span data-testid={`play-${v.id}`} className="absolute inset-0 flex items-center justify-center bg-imi-bg/30 group-hover/film:bg-imi-bg/50 transition-colors">
                        <span className="w-16 h-16 rounded-full bg-imi-gold text-imi-bg flex items-center justify-center group-hover/film:scale-110 transition-transform">
                          <Play size={22} className="ml-1" />
                        </span>
                      </span>
                    )}
                    {v.category && (
                      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] text-imi-gold bg-imi-bg/70 backdrop-blur border border-imi-gold/30 px-2.5 py-1">{v.category}</span>
                    )}
                    {!videoSrc && vEmbed && (
                      <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.2em] text-imi-gold bg-imi-bg/80 backdrop-blur border border-imi-gold/40 px-2 py-0.5">YouTube</span>
                    )}
                  </div>
                  <div className="p-5 flex items-center justify-between">
                    <h3 className="font-serif text-xl text-imi-text">{v.title}</h3>
                    {v.meta ? <span className="text-imi-text/40 text-xs">{v.meta}</span> : null}
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="py-16 text-center text-imi-text/40 border border-imi-border">No films in this category yet.</div>
        )}
      </div>
      {openIdx !== null && (
        <MediaLightbox items={filtered} startIndex={openIdx} onClose={() => setOpenIdx(null)} testIdPrefix="lightbox-film" />
      )}
    </section>
  );
}

/* ---------------- Gallery ---------------- */
export function Gallery({ items, viewAllHref, total, settings }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="gallery" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.gallery_kicker || "Gallery"}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight">{renderEmphasis(settings?.gallery_title || "Frames from *the inside*.")}</h2>
          {settings?.gallery_subtitle && (<p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="gallery-subtitle">{settings.gallery_subtitle}</p>)}
        </Reveal>
        {viewAllHref && total > items.length && (
          <Link to={viewAllHref} data-testid="view-all-gallery" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-5 py-3 hover:bg-imi-gold hover:text-imi-bg transition-colors self-start">
            View full gallery ({total}) <ArrowRight size={16} />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((img, i) => {
          const src = mediaUrl(img) || embedThumbnail(embedSourceUrl(img));
          return (
            <Reveal key={img.id} delay={i * 0.05} className={i % 5 === 0 ? "col-span-2 row-span-2" : ""}>
              <button
                type="button"
                onClick={() => setOpenIdx(i)}
                data-testid={`gallery-img-${img.id}`}
                className="group/img relative overflow-hidden border border-imi-border aspect-[4/5] w-full bg-gradient-to-br from-imi-elevated to-imi-surface focus:outline-none focus:ring-2 focus:ring-imi-gold"
              >
                {src ? (
                  <img src={src} alt={img.title} className="w-full h-full object-cover grayscale group-hover/img:grayscale-0 transition-all duration-700 group-hover/img:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-imi-gold/25 text-[10px] uppercase tracking-[0.2em]">No image</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-imi-bg/80 to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity" />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover/img:opacity-100 transition-opacity translate-y-2 group-hover/img:translate-y-0 duration-500">
                  <span className="font-serif text-lg text-imi-text">{img.title}</span>
                </div>
              </button>
            </Reveal>
          );
        })}
      </div>
      {openIdx !== null && (
        <MediaLightbox items={items} startIndex={openIdx} onClose={() => setOpenIdx(null)} testIdPrefix="lightbox-gallery" />
      )}
    </section>
  );
}

/* ---------------- Journal (articles) ---------------- */
function Journal({ items, settings }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="journal" className="border-t border-imi-border py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.journal_kicker || "The Journal"}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight">{renderEmphasis(settings?.journal_title || "Words that *refuse silence*.")}</h2>
          {settings?.journal_subtitle && (<p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="journal-subtitle">{settings.journal_subtitle}</p>)}
        </Reveal>
        <div className="divide-y divide-imi-border border-y border-imi-border">
          {items.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.05}>
              <button
                type="button"
                onClick={() => setOpenIdx(i)}
                data-testid={`article-${a.id}`}
                className="w-full text-left group/art grid md:grid-cols-12 gap-6 py-8 items-center focus:outline-none focus:bg-imi-elevated/40"
              >
                <div className="md:col-span-1 text-imi-gold font-serif text-2xl">{String(i + 1).padStart(2, "0")}</div>
                <div className="md:col-span-7">
                  <h3 className="font-serif text-2xl md:text-4xl text-imi-text group-hover/art:text-imi-gold transition-colors">{a.title}</h3>
                  <p className="mt-2 text-imi-text/50 max-w-2xl">{a.excerpt}</p>
                </div>
                <div className="md:col-span-3 overflow-hidden aspect-[16/10] border border-imi-border bg-gradient-to-br from-imi-elevated to-imi-surface flex items-center justify-center">
                  {(() => {
                    const aImg = mediaUrl(a) || embedThumbnail(embedSourceUrl(a));
                    return aImg ? (
                      <img src={aImg} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover/art:scale-105" />
                    ) : (
                      <span className="text-imi-gold/25 text-[10px] uppercase tracking-[0.2em]">No image</span>
                    );
                  })()}
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <ArrowRight className="text-imi-text/40 group-hover/art:text-imi-gold group-hover/art:translate-x-1 transition-all" />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      {openIdx !== null && (
        <MediaLightbox items={items} startIndex={openIdx} onClose={() => setOpenIdx(null)} testIdPrefix="lightbox-article" />
      )}
    </section>
  );
}

/* ---------------- Articles preview (original writing, type=post) ---------------- */
function ArticlesPreview({ items, settings }) {
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); } catch { return ""; } };
  const readTime = (body) => Math.max(1, Math.round((body || "").split(/\s+/).filter(Boolean).length / 200));
  const latest = [...items].sort((x, y) => (y.created_at || "").localeCompare(x.created_at || "")).slice(0, 3);
  return (
    <section id="articles" className="border-t border-imi-border bg-imi-surface py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <Reveal className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.articles_kicker || "Articles"}</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">{renderEmphasis(settings?.articles_title || "Written from *the inside out*.")}</h2>
            {settings?.articles_subtitle && <p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed" data-testid="articles-subtitle">{settings.articles_subtitle}</p>}
          </Reveal>
          <Link to="/articles" data-testid="view-all-articles" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-5 py-3 hover:bg-imi-gold hover:text-imi-bg transition-colors self-start">
            View all articles <ArrowRight size={16} />
          </Link>
        </div>
        <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="articles-preview-grid">
          {latest.map((a) => {
            const img = mediaUrl(a);
            return (
              <StaggerItem key={a.id}>
                <Link to={`/articles/${a.id}`} data-testid={`home-article-${a.id}`} className="group block border border-imi-border bg-imi-bg overflow-hidden hover:border-imi-gold/50 transition-colors h-full">
                  <div className="aspect-[16/10] bg-imi-elevated overflow-hidden relative">
                    {img ? (
                      <img src={img} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Quote size={30} className="text-imi-gold/25" /></div>
                    )}
                    {a.category && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] text-imi-gold bg-imi-bg/80 backdrop-blur border border-imi-gold/30 px-2.5 py-1">{a.category}</span>}
                  </div>
                  <div className="p-6">
                    <div className="text-imi-text/40 text-[10px] uppercase tracking-[0.25em]">{fmtDate(a.created_at)} · {readTime(a.body)} min read</div>
                    <h3 className="mt-3 font-serif text-xl md:text-2xl leading-tight group-hover:text-imi-goldlight transition-colors line-clamp-2">{a.title}</h3>
                    {a.excerpt && <p className="mt-3 text-imi-text/55 text-sm leading-relaxed line-clamp-3">{a.excerpt}</p>}
                    <div className="mt-5 pt-4 border-t border-imi-border flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-imi-gold">
                      <span>Read article</span><ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* ---------------- About ---------------- */
function About({ settings }) {
  const aboutImg = settings?.about_image ? mediaUrl({ media_id: settings.about_image })
    : (settings?.founder_image ? mediaUrl({ media_id: settings.founder_image }) : "/founder-harold.png");
  return (
    <section id="about" className="border-t border-imi-border py-24 md:py-36 bg-imi-surface">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <div className="overflow-hidden border border-imi-border aspect-[4/5]">
              <img src={aboutImg} alt="Inside of Mental Injustice" className="w-full h-full object-cover" style={{ objectPosition: "center 25%" }} />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-imi-gold text-imi-bg p-6 max-w-[220px] hidden md:block">
              <Quote size={22} />
              <p className="mt-2 text-sm font-medium leading-snug">&ldquo;I lived it. That is why I will not stop telling it.&rdquo;</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">{settings?.about_kicker || "About IMI"}</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] tracking-tight">{renderEmphasis(settings?.about_title || "")}</h2>
          <div className="mt-8 text-imi-text/70 text-lg leading-relaxed space-y-5 whitespace-pre-line">{settings?.about_body}</div>
          <div className="mt-10 pt-6 border-t border-imi-border flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="font-serif text-2xl text-imi-goldlight">{settings?.founder_name}</div>
              <div className="text-sm uppercase tracking-[0.2em] text-imi-text/40 mt-1">{settings?.founder_role}</div>
            </div>
            <Link to="/harold" data-testid="read-harold-story" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-imi-gold border border-imi-gold/40 px-5 py-3 hover:bg-imi-gold/10 transition-colors">
              Read Harold's full story <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */
function Contact({ settings }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally { setLoading(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <section id="contact" className="py-24 md:py-36 border-t border-imi-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Get involved</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">Move a story <span className="italic gold-gradient-text">forward</span>.</h2>
          <p className="mt-8 text-imi-text/60 text-lg leading-relaxed max-w-md">
            Partner with us, share your testimony, or support the next production. Every message reaches the team directly.
          </p>
          <div className="mt-12 space-y-5">
            <div className="flex items-center gap-4 text-imi-text/70">
              <Mail size={18} className="text-imi-gold" /> {settings?.contact_email || "hello@insidementalinjustice.com"}
            </div>
            <div className="flex items-center gap-4 text-imi-text/70">
              <MapPin size={18} className="text-imi-gold" /> insidementalinjustice.com
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <input required value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Name"
                className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
              <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="Email"
                className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
            </div>
            <input value={form.subject} onChange={set("subject")} data-testid="contact-subject" placeholder="Subject"
              className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full transition-colors" />
            <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" placeholder="Your message"
              className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/40 w-full resize-none transition-colors" />
            <button disabled={loading} type="submit" data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors disabled:opacity-60">
              {loading ? "Sending…" : "Send message"} <Send size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Voices (approved stories) ---------------- */
function VoiceCard({ s }) {
  const media = s.media_id ? mediaUrl({ media_id: s.media_id }) : null;
  return (
    <article data-testid={`voice-${s.id}`} className="border border-imi-border bg-imi-bg p-7 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] uppercase tracking-[0.2em] text-imi-gold border border-imi-gold/30 px-2 py-1">{s.format}</span>
        {s.featured && <Star size={14} className="text-imi-gold fill-imi-gold" />}
        <Quote size={18} className="text-imi-gold/50 ml-auto" />
      </div>
      {media && s.media_type && s.media_type.startsWith("audio") && (
        <audio controls src={media} className="w-full mb-4" />
      )}
      {media && s.media_type && s.media_type.startsWith("video") && (
        <video controls src={media} preload="metadata" className="w-full mb-4 border border-imi-border" />
      )}
      {s.story && <p className="text-imi-text/70 leading-relaxed flex-1 line-clamp-4">{s.story}</p>}
      <div className="mt-6 pt-4 border-t border-imi-border flex items-center justify-between">
        <span className="font-serif text-imi-goldlight text-lg">— {s.alias}</span>
        <Link to={`/stories/${s.id}`} data-testid={`read-voice-${s.id}`} className="text-imi-text/50 hover:text-imi-gold transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em]">
          Read <ArrowRight size={14} />
        </Link>
      </div>
    </article>
  );
}

function Voices({ items, settings }) {
  const featured = items.find((s) => s.featured);
  const rest = featured ? items.filter((s) => s.id !== featured.id) : items;
  const media = featured && featured.media_id ? mediaUrl({ media_id: featured.media_id }) : null;
  return (
    <section id="voices" className="border-t border-imi-border bg-imi-surface py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.voices_kicker || "Voices"}</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">{renderEmphasis(settings?.voices_title || "Stories from *the inside*.")}</h2>
            {settings?.voices_subtitle && (<p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="voices-subtitle">{settings.voices_subtitle}</p>)}
          </Reveal>
          <Link to="/share" data-testid="share-story-cta" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-gold border border-imi-gold/40 px-5 py-3 hover:bg-imi-gold hover:text-imi-bg transition-colors self-start">
            Share your story <ArrowRight size={16} />
          </Link>
        </div>

        {featured && (
          <Reveal className="mb-5">
            <article data-testid={`voice-featured-${featured.id}`} className="border border-imi-gold/40 bg-imi-bg p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <Star size={16} className="text-imi-gold fill-imi-gold" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-imi-gold">Featured Voice · {featured.format}</span>
                </div>
                <Quote size={28} className="text-imi-gold/40 mb-4" />
                <p className="font-serif text-2xl md:text-3xl leading-snug text-imi-text line-clamp-6">{featured.story}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-serif text-imi-goldlight text-xl">— {featured.alias}</span>
                  <Link to={`/stories/${featured.id}`} className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-gold hover:gap-3 transition-all">Read full story <ArrowRight size={16} /></Link>
                </div>
              </div>
              {media && featured.media_type?.startsWith("video") ? (
                <video controls poster={undefined} preload="metadata" src={media} className="w-full border border-imi-border" />
              ) : media && featured.media_type?.startsWith("audio") ? (
                <div className="border border-imi-border p-8 bg-imi-surface"><audio controls src={media} className="w-full" /></div>
              ) : (
                <div className="hidden md:flex items-center justify-center border border-imi-border bg-imi-surface aspect-[4/3]"><Quote size={64} className="text-imi-gold/20" /></div>
              )}
            </article>
          </Reveal>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05}><VoiceCard s={s} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Advertising / Spotlight ---------------- */

// Turn any YouTube/Vimeo URL into a proper embed url
export function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}?rel=0`;
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}?rel=0`;
      if (u.pathname.startsWith("/embed/")) return url;
      if (u.pathname.startsWith("/shorts/")) {
        const sid = u.pathname.split("/").filter(Boolean)[1];
        if (sid) return `https://www.youtube.com/embed/${sid}?rel=0`;
      }
    }
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* ignore */ }
  return url;
}

// Try to derive a thumbnail image from a YouTube/Vimeo URL — used as a fallback
// preview when the admin didn't upload a separate cover image.
export function embedThumbnail(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    let id = null;
    if (host === "youtu.be") id = u.pathname.slice(1);
    else if (host.endsWith("youtube.com")) {
      id = u.searchParams.get("v")
        || (u.pathname.startsWith("/embed/") && u.pathname.split("/")[2])
        || (u.pathname.startsWith("/shorts/") && u.pathname.split("/")[2])
        || null;
    }
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    // Vimeo: no public thumbnail without an API call — skip
  } catch { /* ignore */ }
  return null;
}

// Renders the correct hero-media (video > embed > image) for an ad.
// `fit="contain"` shows the FULL image with a blurred fill-background (Netflix-style)
// so portrait book covers etc. don't get cropped in landscape hero frames.
export function AdMedia({ ad, autoPlay = false, muted = true, fit = "cover", className = "" }) {
  const videoSrc = ad.video_id ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${ad.video_id}` : null;
  const embed = embedSourceUrl(ad) ? toEmbedUrl(embedSourceUrl(ad)) : null;
  const img = mediaUrl(ad);
  if (videoSrc) {
    return (
      <video
        src={videoSrc}
        poster={img || undefined}
        autoPlay={autoPlay}
        muted={muted}
        loop
        playsInline
        controls={!autoPlay}
        preload="metadata"
        className={`w-full h-full object-cover ${className}`}
      />
    );
  }
  if (embed) {
    const src = autoPlay ? `${embed}${embed.includes("?") ? "&" : "?"}autoplay=1&mute=1` : embed;
    return (
      <iframe
        src={src}
        title={ad.title}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className={`w-full h-full ${className}`}
      />
    );
  }
  if (img) {
    if (fit === "contain") {
      return (
        <div className={`relative w-full h-full overflow-hidden bg-imi-bg ${className}`}>
          {/* Blurred fill background so letterboxed areas feel intentional */}
          <img
            src={img}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60"
          />
          <div className="absolute inset-0 bg-imi-bg/40" />
          {/* Foreground: whole image, never cropped */}
          <img
            src={img}
            alt={ad.title}
            className="relative z-10 w-full h-full object-contain"
          />
        </div>
      );
    }
    return <img src={img} alt={ad.title} className={`w-full h-full object-cover ${className}`} />;
  }
  return (
    <div className={`w-full h-full min-h-[200px] flex items-center justify-center bg-imi-elevated ${className}`}>
      <Sparkles className="text-imi-gold/30" size={40} />
    </div>
  );
}

function AdBadges({ ad }) {
  if (!ad.badge && !ad.price) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ad.badge && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-imi-bg bg-imi-gold px-2.5 py-1 font-semibold">
          <Tag size={11} /> {ad.badge}
        </span>
      )}
      {ad.price && (
        <span className="text-[11px] uppercase tracking-[0.2em] text-imi-gold border border-imi-gold/40 px-2.5 py-1">
          {ad.price}
        </span>
      )}
    </div>
  );
}

function AdCTA({ ad, size = "md", testId }) {
  const label = ad.cta_text || ad.meta || "Learn more";
  const link = ad.cta_url || ad.body || ad.external_url || "#";
  if (!link || link === "#") return null;
  const cls = size === "lg"
    ? "px-8 py-4 text-sm"
    : size === "sm"
      ? "px-4 py-2.5 text-[11px]"
      : "px-6 py-3 text-xs";
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className={`group/btn inline-flex items-center gap-2 bg-imi-gold text-imi-bg ${cls} uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors self-start`}>
      {label} <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
    </a>
  );
}

// Full-bleed rotating hero spotlight
function HeroSpotlight({ items }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [items.length]);
  if (!items.length) return null;
  const ad = items[i];
  const next = () => setI((v) => (v + 1) % items.length);
  const prev = () => setI((v) => (v - 1 + items.length) % items.length);
  return (
    <div className="relative border border-imi-gold/25 bg-imi-surface overflow-hidden" data-testid="spotlight-hero">
      {/* Ambient blurred fill fills the entire card so the panel never looks empty */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`bg-${ad.id}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9 }}
          className="absolute inset-0"
          aria-hidden="true"
        >
          <AdMedia ad={ad} autoPlay muted fit="cover" className="scale-125 blur-2xl opacity-40" />
          <div className="absolute inset-0 bg-imi-bg/60" />
        </motion.div>
      </AnimatePresence>

      <div className="relative grid md:grid-cols-2 items-stretch min-h-[420px] md:min-h-[520px]">
        {/* LEFT: Copy */}
        <div className="relative z-10 p-6 md:p-14 flex items-center order-2 md:order-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={`copy-${ad.id}`}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="w-full"
            >
              <AdBadges ad={ad} />
              {ad.subtitle && <p className="mt-3 text-[11px] md:text-sm uppercase tracking-[0.3em] text-imi-gold">{ad.subtitle}</p>}
              <h3 className="mt-3 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight text-imi-text">{ad.title}</h3>
              {ad.excerpt && <p className="mt-4 text-imi-text/70 text-sm md:text-base leading-relaxed line-clamp-4">{ad.excerpt}</p>}
              <div className="mt-6">
                <AdCTA ad={ad} size="lg" testId={`spotlight-cta-${ad.id}`} />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* RIGHT: Full media, contained, never cropped */}
        <div className="relative order-1 md:order-2 min-h-[280px] md:min-h-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={`media-${ad.id}`}
              initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              <AdMedia ad={ad} autoPlay muted fit="contain" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button onClick={prev} aria-label="Previous" data-testid="spotlight-prev"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full border border-imi-gold/40 bg-imi-bg/70 backdrop-blur flex items-center justify-center text-imi-gold hover:bg-imi-gold hover:text-imi-bg transition-colors">
            <ChevronLeft size={20} />
          </button>
          <button onClick={next} aria-label="Next" data-testid="spotlight-next"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full border border-imi-gold/40 bg-imi-bg/70 backdrop-blur flex items-center justify-center text-imi-gold hover:bg-imi-gold hover:text-imi-bg transition-colors">
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-4 right-6 z-20 flex gap-1.5">
            {items.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Go to slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "bg-imi-gold w-8" : "bg-imi-text/30 w-3 hover:bg-imi-text/60"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Compact card used in grids, carousel and sidebar
export function AdCard({ ad, variant = "grid", testIdPrefix = "ad" }) {
  const isLarge = variant === "large";
  const isSidebar = variant === "sidebar";
  return (
    <article
      data-testid={`${testIdPrefix}-${ad.id}`}
      className={`group relative border border-imi-gold/25 bg-imi-surface overflow-hidden hover:border-imi-gold/60 transition-colors duration-500 ${isLarge ? "md:grid md:grid-cols-2" : "flex flex-col"}`}
    >
      <div className={`${isLarge ? "aspect-[4/3] md:aspect-auto" : isSidebar ? "aspect-video" : "aspect-[4/3]"} relative overflow-hidden`}>
        <AdMedia ad={ad} autoPlay={isLarge} fit={isLarge ? "contain" : "cover"} className="transition-transform duration-700 group-hover:scale-[1.04]" />
        <div className="absolute inset-0 bg-gradient-to-t from-imi-surface/80 via-transparent to-transparent pointer-events-none" />
        {ad.badge && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-imi-bg bg-imi-gold px-2.5 py-1 font-semibold">
            <Tag size={11} /> {ad.badge}
          </span>
        )}
        {ad.price && (
          <span className="absolute top-3 right-3 text-[11px] uppercase tracking-[0.2em] text-imi-gold bg-imi-bg/80 backdrop-blur border border-imi-gold/40 px-2.5 py-1">
            {ad.price}
          </span>
        )}
      </div>
      <div className={`${isLarge ? "p-8 md:p-12" : isSidebar ? "p-4" : "p-6"} flex flex-col justify-center`}>
        {ad.subtitle && <p className={`uppercase tracking-[0.25em] text-imi-gold ${isSidebar ? "text-[9px]" : "text-[10px]"} mb-2`}>{ad.subtitle}</p>}
        <h3 className={`font-serif text-imi-text tracking-tight leading-[1.1] ${isLarge ? "text-3xl md:text-5xl" : isSidebar ? "text-lg" : "text-2xl"}`}>{ad.title}</h3>
        {ad.excerpt && !isSidebar && <p className={`mt-3 text-imi-text/60 leading-relaxed ${isLarge ? "text-base md:text-lg max-w-xl" : "text-sm line-clamp-3"}`}>{ad.excerpt}</p>}
        <div className={`${isLarge ? "mt-6" : "mt-4"}`}>
          <AdCTA ad={ad} size={isLarge ? "lg" : isSidebar ? "sm" : "md"} testId={`${testIdPrefix}-cta-${ad.id}`} />
        </div>
      </div>
    </article>
  );
}

// Horizontal swipeable carousel band
function AdCarousel({ items }) {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };
  if (!items.length) return null;
  return (
    <div className="relative" data-testid="ad-carousel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 text-imi-text/60 text-sm uppercase tracking-[0.25em]"><ShoppingBag size={14} className="text-imi-gold" /> More from the store</div>
        <div className="hidden md:flex gap-2">
          <button onClick={() => scrollBy(-1)} aria-label="Scroll left" data-testid="carousel-prev"
            className="h-9 w-9 border border-imi-border hover:border-imi-gold text-imi-text/60 hover:text-imi-gold flex items-center justify-center transition-colors">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => scrollBy(1)} aria-label="Scroll right" data-testid="carousel-next"
            className="h-9 w-9 border border-imi-border hover:border-imi-gold text-imi-text/60 hover:text-imi-gold flex items-center justify-center transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 md:-mx-10 px-6 md:px-10 scroll-smooth"
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((ad) => (
          <div key={ad.id} className="snap-start shrink-0 w-[85%] sm:w-[55%] md:w-[38%] lg:w-[28%]">
            <AdCard ad={ad} variant="grid" testIdPrefix="ad-carousel-item" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Compact vertical sidebar for Videos/Stories pages
export function AdSidebar({ items = [], title = "Spotlight" }) {
  const list = (items || []).filter((a) => a.active !== false).slice(0, 3);
  if (!list.length) return null;
  return (
    <aside data-testid="ad-sidebar" className="space-y-4">
      <div className="flex items-center gap-2 text-imi-gold text-[10px] uppercase tracking-[0.3em]"><Sparkles size={12} /> {title}</div>
      {list.map((ad) => <AdCard key={ad.id} ad={ad} variant="sidebar" testIdPrefix="ad-sidebar" />)}
    </aside>
  );
}

export function Promo({ items }) {
  const active = (items || []).filter((a) => a.active !== false);
  if (!active.length) return null;
  const featured = active.filter((a) => a.featured);
  const others = active.filter((a) => !a.featured);
  // Spotlight uses featured ads if any, else the first two
  const heroList = (featured.length ? featured : active).slice(0, 5);
  // Grid: 1 big + remaining smalls (up to 3)
  const gridBase = featured.length ? others : active.slice(heroList.length);
  const gridLarge = gridBase[0];
  const gridSmalls = gridBase.slice(1, 4);
  // Carousel = everything active
  const carousel = active;

  return (
    <section id="spotlight" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-imi-gold/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-imi-golddark/5 blur-3xl" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="mb-12 md:mb-14 text-center max-w-2xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">Spotlight &amp; Store</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Books, films &amp; <span className="italic gold-gradient-text">partners</span>.</h2>
          <p className="mt-5 text-imi-text/60 text-sm md:text-base max-w-lg mx-auto">Handpicked works and campaigns worth your time — from our founder, our partners, and our community.</p>
        </Reveal>

        {/* 1. Auto-rotating hero */}
        {heroList.length > 0 && (
          <Reveal className="mb-14">
            <HeroSpotlight items={heroList} />
          </Reveal>
        )}

        {/* 2. Featured (large) + secondary grid combo */}
        {gridLarge && (
          <Reveal className="mb-14">
            <AdCard ad={gridLarge} variant="large" testIdPrefix="ad-featured" />
            {gridSmalls.length > 0 && (
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {gridSmalls.map((ad) => <AdCard key={ad.id} ad={ad} testIdPrefix="ad-grid" />)}
              </div>
            )}
          </Reveal>
        )}

        {/* 3. Horizontal carousel of everything */}
        {carousel.length > 1 && (
          <Reveal>
            <AdCarousel items={carousel} />
          </Reveal>
        )}

        <div className="mt-14 text-center">
          <Link to="/shop" data-testid="visit-shop"
            className="inline-flex items-center gap-3 border border-imi-gold text-imi-gold px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-imi-gold hover:text-imi-bg transition-colors">
            <ShoppingBag size={16} /> Visit the full store
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Support / Donate ---------------- */
const TIERS = [
  { id: "supporter", label: "Supporter", amount: 25 },
  { id: "advocate", label: "Advocate", amount: 50 },
  { id: "producer", label: "Producer", amount: 100 },
  { id: "champion", label: "Champion", amount: 250 },
];
function Support({ settings }) {
  const [tier, setTier] = useState("advocate");
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const paypalHandle = (settings?.paypal_handle || "").trim();
  const paypalClientId = (settings?.paypal_client_id || "").trim();
  const paypalButtonId = (settings?.paypal_hosted_button_id || "").trim();
  const paypalHostedEnabled = settings?.paypal_enabled !== false && !!paypalClientId && !!paypalButtonId;
  const paypalEnabled = settings?.paypal_enabled !== false && (paypalHostedEnabled || !!paypalHandle);

  // Load PayPal Hosted Button SDK once when settings arrive
  useEffect(() => {
    if (!paypalHostedEnabled) return;
    const scriptId = "paypal-sdk";
    const render = () => {
      if (!window.paypal?.HostedButtons) return;
      const el = document.getElementById(`paypal-container-${paypalButtonId}`);
      if (el && !el.dataset.rendered) {
        el.innerHTML = "";
        window.paypal.HostedButtons({ hostedButtonId: paypalButtonId })
          .render(`#paypal-container-${paypalButtonId}`);
        el.dataset.rendered = "1";
      }
    };
    if (document.getElementById(scriptId)) {
      render();
      return;
    }
    const s = document.createElement("script");
    s.id = scriptId;
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(paypalClientId)}&components=hosted-buttons&enable-funding=venmo&currency=USD`;
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }, [paypalHostedEnabled, paypalClientId, paypalButtonId]);
  const cashEnabled = false; // Cash App temporarily removed from the donation section
  const venmoHandle = (settings?.venmo_handle || "").replace(/^@/, "").trim();
  const venmoEnabled = settings?.venmo_enabled !== false && !!venmoHandle;
  // Only expose the Stripe/card button when the backend is running in LIVE Stripe mode.
  // Otherwise donors get silently declined which is exactly what happened to Harold's supporters.
  const stripeLive = (settings?.stripe_mode || "test") === "live";
  const amount = custom && Number(custom) > 0
    ? Number(custom)
    : (TIERS.find((t) => t.id === tier)?.amount || 50);
  const note = "Donation to Inside of Mental Injustice";
  // PayPal Business accounts don't pre-fill amounts via /USUSD suffix — use the classic
  // paypal.me/{handle}/{amount} format which works for both Personal and Business.
  const paypalUrl = paypalEnabled ? `https://www.paypal.com/paypalme/${paypalHandle}/${amount}` : "";
  const venmoUrl = venmoEnabled
    ? `https://venmo.com/${venmoHandle}?txn=pay&amount=${amount}&note=${encodeURIComponent(note)}`
    : "";
  const altCount = [paypalEnabled, cashEnabled, venmoEnabled].filter(Boolean).length;

  const donate = async () => {
    setLoading(true);
    try {
      const body = { origin_url: window.location.origin, name: donor.name, email: donor.email };
      if (custom && Number(custom) > 0) body.amount = Number(custom);
      else body.tier = tier;
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="support" className="border-t border-imi-border py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Support the mission</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">Fund the <span className="italic gold-gradient-text">next story</span>.</h2>
          <p className="mt-8 text-imi-text/60 text-lg leading-relaxed max-w-md">
            Every contribution funds films, investigations and the survivors who tell them. Give once — and help
            turn lived experience into lasting reform.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-imi-text/40 text-sm">
            {paypalEnabled && <span className="inline-flex items-center gap-2"><Lock size={16} className="text-imi-gold" /> PayPal</span>}
            {(venmoEnabled || paypalHostedEnabled) && <span className="inline-flex items-center gap-2"><Lock size={16} className="text-imi-gold" /> Venmo</span>}
            {cashEnabled && <span className="inline-flex items-center gap-2"><Lock size={16} className="text-imi-gold" /> Cash App</span>}
            {paypalHostedEnabled && <span className="inline-flex items-center gap-2"><Lock size={16} className="text-imi-gold" /> Credit / Debit Card</span>}
            <span className="inline-flex items-center gap-2"><Lock size={16} className="text-imi-gold" /> Card via Stripe</span>
          </div>
          <div className="mt-10 max-w-md">
            <ShareQR variant="card" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-imi-border bg-imi-surface p-8 md:p-10">
            {/* When the PayPal Hosted Button is active, PayPal renders its own amount input inside the iframe.
                Showing our tier + custom-amount picker as well confuses donors (two amount fields). Hide them. */}
            {!paypalHostedEnabled && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {TIERS.map((t) => (
                    <button key={t.id} data-testid={`tier-${t.id}`}
                      onClick={() => { setTier(t.id); setCustom(""); }}
                      className={`p-4 border text-left transition-colors ${!custom && tier === t.id ? "border-imi-gold bg-imi-gold/10" : "border-imi-border hover:border-imi-text/40"}`}>
                      <div className="font-serif text-2xl text-imi-goldlight">${t.amount}</div>
                      <div className="text-[11px] uppercase tracking-[0.15em] text-imi-text/50 mt-1">{t.label}</div>
                    </button>
                  ))}
                </div>
                <div className="mb-6">
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Or enter a custom amount (USD)</label>
                  <div className="flex items-center border-b border-imi-border focus-within:border-imi-gold transition-colors">
                    <span className="text-imi-gold text-xl pr-2">$</span>
                    <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="custom-amount" placeholder="100"
                      className="bg-transparent outline-none py-3 text-imi-text placeholder:text-imi-text/30 w-full" />
                  </div>
                </div>
              </>
            )}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <input value={donor.name} onChange={(e) => setDonor((d) => ({ ...d, name: e.target.value }))} data-testid="donor-name" placeholder="Name (optional)"
                className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/30 transition-colors" />
              <input type="email" value={donor.email} onChange={(e) => setDonor((d) => ({ ...d, email: e.target.value }))} data-testid="donor-email" placeholder="Email (optional)"
                className="bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/30 transition-colors" />
            </div>

            {/* Primary — PayPal (hosted button with Venmo + Card built in, or paypal.me fallback) */}
            {paypalHostedEnabled ? (
              <div className="w-full bg-imi-elevated border border-imi-gold/25 p-4" data-testid="donate-paypal-hosted">
                <div className="mb-3 text-[11px] uppercase tracking-[0.25em] text-imi-gold font-semibold text-center">Enter amount below · PayPal / Venmo / Card accepted</div>
                <div id={`paypal-container-${paypalButtonId}`} className="min-h-[52px]" />
              </div>
            ) : paypalEnabled ? (
              <>
                <a href={paypalUrl} target="_blank" rel="noopener noreferrer" data-testid="donate-paypal"
                   className="w-full inline-flex items-center justify-center gap-3 bg-imi-gold text-imi-bg py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors">
                  Donate ${amount} via PayPal <Heart size={16} />
                </a>
                <p data-testid="paypal-ff-note" className="mt-3 text-center text-imi-gold/80 text-xs leading-relaxed">
                  <Sparkles size={12} className="inline -mt-0.5 mr-1 text-imi-gold" />
                  On the next screen, select <span className="font-semibold text-imi-goldlight">"Friends &amp; Family"</span> so 100% of your gift reaches the mission.
                </p>
              </>
            ) : null}
            {venmoEnabled && (
              <div className="mt-3 grid gap-3 grid-cols-1">
                <a href={venmoUrl} target="_blank" rel="noopener noreferrer" data-testid="donate-venmo"
                   className="inline-flex items-center justify-center gap-2 bg-[#3d95ce] text-white py-4 text-sm uppercase tracking-[0.12em] font-semibold hover:bg-[#2c7fb2] transition-colors">
                  Venmo · ${amount}
                </a>
              </div>
            )}

            <button onClick={donate} disabled={loading || !stripeLive} data-testid="donate-submit"
              className={`w-full mt-3 inline-flex items-center justify-center gap-3 py-4 text-sm uppercase tracking-[0.15em] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${altCount > 0 ? "border border-imi-gold/50 text-imi-gold hover:bg-imi-gold hover:text-imi-bg bg-transparent" : "bg-imi-gold text-imi-bg hover:bg-imi-goldlight"}`}>
              {loading ? "Redirecting to Stripe…" : stripeLive ? `Donate $${amount} via card (Stripe)` : "Card payments coming soon"} <Heart size={16} />
            </button>

            {!stripeLive && (
              <p className="mt-2 text-imi-text/40 text-xs text-center">Credit/debit card processing is being activated. Please use PayPal{cashEnabled ? ", Cash App" : ""}{venmoEnabled ? " or Venmo" : ""} in the meantime.</p>
            )}

            {altCount > 0 && stripeLive && (
              <p className="mt-4 text-imi-text/40 text-xs text-center">
                PayPal / Venmo open in a new tab · Card option processes on our site
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Page ---------------- */
export default function Home({ settings }) {
  const [content, setContent] = useState({ production: [], video: [], image: [], article: [], ad: [], post: [] });
  const [stories, setStories] = useState([]);
  useEffect(() => {
    api.get("/content?visible=true").then((r) => {
      const grouped = { production: [], video: [], image: [], article: [], ad: [], post: [] };
      r.data.forEach((it) => { (grouped[it.type] = grouped[it.type] || []).push(it); });
      setContent(grouped);
    });
    api.get("/stories/public").then((r) => setStories(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <Hero settings={settings} />
      <PressStrip settings={settings} />
      <MissionBand settings={settings} />
      <About settings={settings} />
      <Ambassadors settings={settings} />
      <Biography settings={settings} />
      <StillInside settings={settings} />
      <TellTheirStoryCTA />
      <Band text={settings?.marquee_text} />
      <Stats stats={settings?.stats} />
      <Manifesto settings={settings} />
      <SixByNineCell settings={settings} />
      {content.production.length > 0 && <Productions items={content.production} settings={settings} />}
      {content.video.length > 0 && <Films items={content.video.slice(0, 3)} total={content.video.length} viewAllHref="/videos" settings={settings} />}
      {content.image.length > 0 && <Gallery items={content.image.slice(0, 8)} total={content.image.length} viewAllHref="/gallery" settings={settings} />}
      {content.article.length > 0 && <Journal items={content.article} settings={settings} />}
      {(content.post || []).length > 0 && <ArticlesPreview items={content.post} settings={settings} />}
      {(content.ad || []).length > 0 && <Promo items={content.ad} />}
      {stories.length > 0 && <Voices items={stories} settings={settings} />}
      <Support settings={settings} />
      <ManifestoWall />
      <Contact settings={settings} />
    </>
  );
}
